import React, { useCallback, useEffect, useState } from 'react';
import { getScriptRunConfigs, updateScriptRunConfig } from '../../api/adminScriptRunConfigs';
import { buildScriptConfigPayload, getInvalidScriptConfigScopes, scriptConfigToForm } from '../../helpers/script_runner_helpers';
import Loader from '../General/Loader';
import './ScriptConfig.scss';

const emptyConfig = () => ({
  scriptPath: '',
  name: '',
  description: '',
  allowedScopes: '',
  allowedUserIds: '',
  mustDryRun: false,
  maxRuntimeSeconds: '3600',
  isEnabled: true,
  args: []
});


const commaSeparatedValues = value => value.split(',').map(item => item.trim()).filter(Boolean);

const ScriptConfig = () => {
  const [configs, setConfigs] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyConfig);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const loadConfigs = useCallback(() => {
    setIsLoading(true);
    getScriptRunConfigs()
      .then(response => {
        const rows = Array.isArray(response) ? response : response?.data || response?.configurations || [];
        const sortedRows = [...rows].sort((a, b) => a.name.localeCompare(b.name));
        setConfigs(sortedRows);
        if (sortedRows.length) {
          setSelectedId(sortedRows[0].id);
          setForm(scriptConfigToForm(sortedRows[0]));
        }
      })
      .catch(err => setError(typeof err === 'string' ? err : 'Failed to load script configurations.'))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  const updateField = (field, value) => setForm(current => ({ ...current, [field]: value }));

  const selectConfig = config => {
    setSelectedId(config.id);
    setForm(scriptConfigToForm(config));
    setError(null);
  };


  const updateArg = (index, field, value) => {
    setForm(current => ({
      ...current,
      args: current.args.map((arg, argIndex) => (argIndex === index ? { ...arg, [field]: value } : arg))
    }));
  };

  const addArg = () => {
    setForm(current => ({
      ...current,
      args: [...current.args, { name: '', flag: '', description: '', type: 'string', required: false, takesValue: true, options: '' }]
    }));
  };

  const removeArg = index => {
    setForm(current => ({ ...current, args: current.args.filter((_, argIndex) => argIndex !== index) }));
  };


  const handleSave = async event => {
    event.preventDefault();
    setError(null);
    if (!form.scriptPath.trim() || !form.name.trim()) return setError('Script path and name are required.');
    if (!Number.isInteger(Number(form.maxRuntimeSeconds)) || Number(form.maxRuntimeSeconds) < 1) {
      return setError('Maximum runtime must be a positive whole number of seconds.');
    }
    if (commaSeparatedValues(form.allowedUserIds).some(id => !/^\d+$/.test(id))) {
      return setError('Allowed user IDs must be comma-separated numbers.');
    }
    const invalidScopes = getInvalidScriptConfigScopes(commaSeparatedValues(form.allowedScopes));
    if (invalidScopes.length) return setError(`Unknown admin scopes: ${invalidScopes.join(', ')}`);
    if (form.args.some(arg => !arg.name.trim() || !arg.flag.trim())) {
      return setError('Every argument needs a name and flag.');
    }
    const flags = form.args.map(arg => arg.flag.trim());
    if (new Set(flags).size !== flags.length) return setError('Argument flags must be unique.');

    setIsSaving(true);
    try {
      const payload = buildScriptConfigPayload(form);
      const saved = await updateScriptRunConfig(selectedId, payload);
      setConfigs(current => current.map(config => (config.id === saved.id ? saved : config)).sort((a, b) => a.name.localeCompare(b.name)));
      setSelectedId(saved.id);
      setForm(scriptConfigToForm(saved));
      window.ALERT.success('Script configuration saved.');
    } catch (err) {
      setError(typeof err === 'string' ? err : 'Failed to save script configuration.');
    } finally {
      setIsSaving(false);
    }
  };


  if (isLoading) {
    return (
      <div className='script-config'>
        <Loader />
      </div>
    );
  }

  return (
    <div className='script-config'>
      <div className='script-config-header'>
        <div>
          <h1>ScriptConfig</h1>
          <p>Edit existing script configurations and their argument fields.</p>
        </div>
      </div>

      {error && <div className='script-config-error'>{error}</div>}

      <div className='script-config-layout'>
        <aside className='script-config-list'>
          <h2>Configurations ({configs.length})</h2>
          {configs.length ? (
            configs.map(config => (
              <button
                type='button'
                key={config.id}
                className={`script-config-list-item${selectedId === config.id ? ' selected' : ''}`}
                onClick={() => selectConfig(config)}
              >
                <span className='script-config-list-name'>{config.name}</span>
                <span className='script-config-list-path'>{config.scriptPath}</span>
                <span className={`script-config-status${config.isEnabled ? ' enabled' : ''}`}>{config.isEnabled ? 'Enabled' : 'Disabled'}</span>
              </button>
            ))
          ) : (
            <div className='script-config-empty'>No script configurations yet.</div>
          )}
        </aside>

        {selectedId != null ? (
          <form className='script-config-editor' onSubmit={handleSave}>
            <div className='script-config-editor-header'>
              <h2>Edit Configuration</h2>
            </div>

            <div className='script-config-form-grid'>
              <label>
                Name *
                <input value={form.name} onChange={event => updateField('name', event.target.value)} required />
              </label>
              <label>
                Script path *
                <input
                  value={form.scriptPath}
                  onChange={event => updateField('scriptPath', event.target.value)}
                  placeholder='Relative to node/scripts'
                  required
                />
              </label>
            </div>

            <label className='script-config-form-field'>
              Description
              <textarea rows='2' value={form.description || ''} onChange={event => updateField('description', event.target.value)} />
            </label>

            <div className='script-config-form-grid'>
              <label>
                Allowed admin scopes
                <input
                  value={form.allowedScopes}
                  onChange={event => updateField('allowedScopes', event.target.value)}
                  placeholder='run_scripts, manage_users'
                />
              </label>
              <label>
                Allowed user IDs
                <input
                  value={form.allowedUserIds}
                  onChange={event => updateField('allowedUserIds', event.target.value)}
                  placeholder='Comma-separated IDs'
                />
              </label>
            </div>

            <div className='script-config-form-grid'>
              <label>
                Maximum runtime (seconds)
                <input
                  type='number'
                  min='1'
                  step='1'
                  value={form.maxRuntimeSeconds}
                  onChange={event => updateField('maxRuntimeSeconds', event.target.value)}
                  required
                />
              </label>
              <div className='script-config-checkboxes'>
                <label>
                  <input type='checkbox' checked={Boolean(form.isEnabled)} onChange={event => updateField('isEnabled', event.target.checked)} />
                  Enabled
                </label>
                <label>
                  <input type='checkbox' checked={Boolean(form.mustDryRun)} onChange={event => updateField('mustDryRun', event.target.checked)} />
                  Require a successful dry run
                </label>
              </div>
            </div>

            <div className='script-config-args-header'>
              <div>
                <h3>Argument fields</h3>
                <p>Define the inputs shown in Script Runner.</p>
              </div>
              <button type='button' className='script-config-button' onClick={addArg}>
                + Add Field
              </button>
            </div>

            {form.args.map((arg, index) => (
              <div className='script-config-arg' key={`${selectedId}-${index}`}>
                <div className='script-config-arg-header'>
                  <strong>Field {index + 1}</strong>
                  <button type='button' className='script-config-remove-field' onClick={() => removeArg(index)}>
                    Remove field
                  </button>
                </div>
                <div className='script-config-form-grid'>
                  <label>
                    Display name *
                    <input value={arg.name} onChange={event => updateArg(index, 'name', event.target.value)} required />
                  </label>
                  <label>
                    CLI flag *
                    <input value={arg.flag} onChange={event => updateArg(index, 'flag', event.target.value)} placeholder='user-id' required />
                  </label>
                </div>
                <div className='script-config-form-grid'>
                  <label>
                    Type
                    <select value={arg.type} onChange={event => updateArg(index, 'type', event.target.value)}>
                      <option value='string'>String</option>
                      <option value='number'>Number</option>
                      <option value='boolean'>Boolean</option>
                      <option value='option'>Option</option>
                      <option value='user'>User</option>
                      <option value='brand'>Brand</option>
                    </select>
                  </label>
                  <label>
                    Description
                    <input value={arg.description} onChange={event => updateArg(index, 'description', event.target.value)} />
                  </label>
                </div>
                {arg.type === 'option' && (
                  <label className='script-config-form-field'>
                    Allowed options
                    <input
                      value={arg.options || ''}
                      onChange={event => updateArg(index, 'options', event.target.value)}
                      placeholder='Option A, Option B'
                    />
                  </label>
                )}
                <div className='script-config-checkboxes'>
                  <label>
                    <input type='checkbox' checked={Boolean(arg.required)} onChange={event => updateArg(index, 'required', event.target.checked)} />
                    Required
                  </label>
                  {arg.type !== 'boolean' && (
                    <label>
                      <input type='checkbox' checked={arg.takesValue !== false} onChange={event => updateArg(index, 'takesValue', event.target.checked)} />
                      Takes a value
                    </label>
                  )}
                </div>
              </div>
            ))}

            <div className='script-config-footer'>
              <button type='submit' className='script-config-button primary' disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        ) : (
          <div className='script-config-editor script-config-empty-selection'>
            {configs.length ? 'Select a configuration to edit.' : 'No script configurations are available to edit.'}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScriptConfig;
